import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from "v-select2-multiple-component";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";
import ValidationService from "@/services/ValidationService";

const maxSize5MB = (value) => !value || value.size <= 5 * 1024 * 1024;

export default {
  name: "examImport",
  components: {
    MainLayout,
    Select2Multiple,
    flatPickr,
    Loading,
  },

  mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

  data() {
    return {
      isLoading: false,
      submitted: false,
      templateSubmitted: false,
      disableImportBtn: false,
      disableConfirmBtn: false,
      disableInputFields: false,
      disableAcademicYear: false,
      importStep: "form",
      academicYearArr: [],
      template_academic_year_id: null,
      academic_year_id: null,
      file_name: "",
      selectedFile: null,
      invalidFileType: false,
      previewRows: [],
      previewTotal: 0,
    };
  },

  validations: {
    academic_year_id: { required },
    selectedFile: { required, maxSize5MB },
  },

  computed: {
    disabledTemplateBtn() {
      return this.disableInputFields;
    },
    isPreviewStep() {
      return this.importStep === "preview";
    },
    selectedAcademicYearName() {
      const selected = this.academicYearArr.find(
        (period) => String(period.id) === String(this.academic_year_id)
      );

      return selected?.text || this.previewRows[0]?.academic_year_name || "";
    },
  },
  watch: {
    academic_year_id() {
      if (this.isPreviewStep) {
        this.clearPreviewState();
      }
    },
  },

  methods: {
    getAcademicYear() {
      this.isLoading = true;
      axios.get("period/periodList")
        .then((res) => {
          this.academicYearArr = res.data.data || [];
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    chooseFile() {
      if (this.isPreviewStep) {
        return;
      }
      this.$refs.fileUploader.value = null;
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.clearPreviewState();
      this.$refs.fileUploader.click();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.clearPreviewState();

      if (!file) {
        return;
      }

      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!["xls", "xlsx"].includes(extension)) {
        this.invalidFileType = true;
        return;
      }

      this.selectedFile = file;
      this.file_name = file.name;
    },

    templateDownload() {
      this.templateSubmitted = true;

      if (this.disabledTemplateBtn) {
        return;
      }

      const params = {};
      if (this.template_academic_year_id) {
        params.academic_year_id = this.template_academic_year_id;
      }

      this.isLoading = true;
      axios.get("exam/excelTemplateDownload", {
        params,
        responseType: "blob",
      })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const link = document.createElement("a");

          link.href = window.URL.createObjectURL(blob);
          link.download = this.getTemplateFileName();
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          console.log(error)
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getTemplateFileName() {
      const selected = this.academicYearArr.find(
        (academicYearArr) => String(academicYearArr.id) === String(this.template_academic_year_id)
      );
      const periodName = (selected?.text || "all").replace(/\s+/g, "_");
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      return `${periodName}_exams_${year}_${month}_${day}.xlsx`;
    },

    confirmImport() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid || this.invalidFileType) {
        return;
      }

      this.validateImport();
    },

    validateImport() {
      this.isLoading = true;
      this.disableImportBtn = true;
      this.disableInputFields = true;
      this.disableAcademicYear = true;

      const formData = new FormData();
      formData.append("academic_year_id", this.academic_year_id);
      formData.append("file", this.selectedFile);

      axios.post("exam/excelImportValidate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((response) => {
          this.previewRows = this.clonePreviewRows(response.data.preview || []);
          this.previewTotal = response.data.total || this.previewRows.length;
          this.clearImportConfirmErrors();
          this.importStep = "preview";
        })
        .catch((error) => {
          this.handleImportValidationError(error);
          this.disableInputFields = false;
          this.disableAcademicYear = false;
          this.disableImportBtn = false;
          this.importStep = "form";
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImportSubmit() {
      this.clearImportConfirmErrors();

      if (!this.previewRows.length) {
        this.$notification.error("No preview data to import.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      const fieldErrors = this.validatePreviewBeforeConfirm();
      if (Object.keys(fieldErrors).length) {
        this.setConfirmFieldErrors(fieldErrors);
        return;
      }

      this.isLoading = true;
      this.disableConfirmBtn = true;
      axios.post("exam/excelImportConfirm", {
        academic_year_id: this.academic_year_id,
        preview: this.buildConfirmPreviewPayload(),
      })
        .then((response) => {
          this.$notification.success(response.data.message || "Exam import completed successfully.", {
            timer: 3,
            position: "bottomCenter",
          });
          this.$router.push({ name: "examList" });
        })
        .catch((error) => {
          this.handleConfirmBackendError(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.disableConfirmBtn = false;
        });
    },

    clonePreviewRows(rows) {
      return (rows || []).map((row) => ({
        row_no: row.row_no,
        term_name: String(row.term_name || "").trim(),
        class_name: String(row.class_name || "").trim(),
        exam_name: String(row.exam_name || "").trim(),
        exam_type_label: String(row.exam_type_label || "").trim(),
        subject_name: String(row.subject_name || "").trim(),
        exam_start_date: row.exam_start_date || "",
        exam_end_date: row.exam_end_date || "",
        pass_mark: row.pass_mark ?? "",
        distinction_mark: row.distinction_mark ?? "",
        full_mark: row.full_mark ?? "",
      }));
    },

    buildConfirmPreviewPayload() {
      return this.previewRows.map((row, index) => ({
        row_no: row.row_no ?? (index + 1),
        term_name: String(row.term_name || "").trim(),
        class_name: String(row.class_name || "").trim(),
        exam_name: String(row.exam_name || "").trim(),
        exam_type_label: String(row.exam_type_label || "").trim(),
        subject_name: String(row.subject_name || "").trim(),
        exam_start_date: row.exam_start_date || "",
        exam_end_date: row.exam_end_date || "",
        pass_mark: this.normalizeMarkValue(row.pass_mark),
        distinction_mark: this.normalizeMarkValue(row.distinction_mark),
        full_mark: this.normalizeMarkValue(row.full_mark),
      }));
    },

    normalizeMarkValue(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") {
          return null;
        }
        return trimmed;
      }

      return value;
    },

    validatePreviewBeforeConfirm() {
      const errors = {};
      const numericPattern = /^-?\d+(\.\d+)?$/;

      this.previewRows.forEach((row, index) => {
        const rowErrors = {};
        const termName = String(row.term_name || "").trim();
        const className = String(row.class_name || "").trim();
        const examName = String(row.exam_name || "").trim();
        const examTypeLabel = String(row.exam_type_label || "").trim();
        const subjectName = String(row.subject_name || "").trim();
        const startDate = String(row.exam_start_date || "").trim();
        const endDate = String(row.exam_end_date || "").trim();

        if (!termName) {
          rowErrors.term_name = "Term Name is required.";
        }
        if (!className) {
          rowErrors.class_name = "Class Name is required.";
        }
        if (!examName) {
          rowErrors.exam_name = "Exam Name is required.";
        } else if (examName.length > 255) {
          rowErrors.exam_name = "Max 255 characters.";
        }
        if (!examTypeLabel) {
          rowErrors.exam_type_label = "Exam Type is required.";
        }
        if (!subjectName) {
          rowErrors.subject_name = "Subject Name is required.";
        }
        if (!startDate) {
          rowErrors.exam_start_date = "Start Date is required.";
        }
        if (!endDate) {
          rowErrors.exam_end_date = "End Date is required.";
        } else if (startDate && endDate < startDate) {
          rowErrors.exam_end_date = "Must be after or equal start date.";
        }

        const passMark = this.normalizeMarkValue(row.pass_mark);
        const distinctionMark = this.normalizeMarkValue(row.distinction_mark);
        const fullMark = this.normalizeMarkValue(row.full_mark);

        if (passMark !== null && !numericPattern.test(String(passMark))) {
          rowErrors.pass_mark = "Must be numeric.";
        }
        if (distinctionMark !== null && !numericPattern.test(String(distinctionMark))) {
          rowErrors.distinction_mark = "Must be numeric.";
        }
        if (fullMark !== null && !numericPattern.test(String(fullMark))) {
          rowErrors.full_mark = "Must be numeric.";
        }

        const passMarkNum = passMark !== null && numericPattern.test(String(passMark)) ? Number(passMark) : null;
        const distinctionMarkNum = distinctionMark !== null && numericPattern.test(String(distinctionMark)) ? Number(distinctionMark) : null;
        const fullMarkNum = fullMark !== null && numericPattern.test(String(fullMark)) ? Number(fullMark) : null;

        if (passMarkNum !== null && (passMarkNum < 0 || passMarkNum > 100)) {
          rowErrors.pass_mark = "Must be 0 to 100.";
        }
        if (distinctionMarkNum !== null && (distinctionMarkNum < 0 || distinctionMarkNum > 100)) {
          rowErrors.distinction_mark = "Must be 0 to 100.";
        }
        if (fullMarkNum !== null && (fullMarkNum < 0 || fullMarkNum > 100)) {
          rowErrors.full_mark = "Must be 0 to 100.";
        }

        if (fullMarkNum !== null) {
          if (passMarkNum !== null && passMarkNum > fullMarkNum && !rowErrors.pass_mark) {
            rowErrors.pass_mark = "Must not be greater than full mark.";
          }
          if (distinctionMarkNum !== null && distinctionMarkNum > fullMarkNum && !rowErrors.distinction_mark) {
            rowErrors.distinction_mark = "Must not be greater than full mark.";
          }
        }

        if (Object.keys(rowErrors).length) {
          errors[index] = rowErrors;
        }
      });

      return errors;
    },

    clearPreviewState() {
      this.importStep = "form";
      this.previewRows = [];
      this.previewTotal = 0;
      this.clearImportConfirmErrors();
      this.disableConfirmBtn = false;
      this.disableInputFields = false;
      this.disableAcademicYear = false;
    },

    resetAll() {
      this.submitted = false;
      this.templateSubmitted = false;
      this.disableImportBtn = false;
      this.template_academic_year_id = null;
      this.academic_year_id = null;
      this.file_name = "";
      this.selectedFile = null;
      this.invalidFileType = false;
      this.clearPreviewState();
      this.$v.$reset();
      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
      this.getAcademicYear();
    },

    showList() {
      this.$router.push({ name: "examList" });
    },

    handleError(error) {
      if (error.response?.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
        return;
      }

      const message = error.response?.data?.message || "Something went wrong.";
      this.$notification.error(message, {
        timer: 3,
        position: "bottomCenter",
      });
    },

    handleImportValidationError(error) {
      if (error.response?.status === 422) {
        const data = error.response.data || {};
        const customMessages = data.errors?.custom_validation_messages;
        const messages = Array.isArray(customMessages) && customMessages.length
          ? customMessages
          : Array.isArray(data.errors) && data.errors.length
            ? data.errors
            : [data.message || data.msg || "Import validation failed."];

        window.alert(messages.join("\n"));
        return;
      }

      this.handleError(error);
    },

    handleConfirmBackendError(error) {
      if (this.applyConfirmBackendErrors(error, this.getImportConfirmErrorOptions())) {
        return;
      }
      this.handleError(error);
    },

    getImportConfirmErrorOptions() {
      return { rowKey: "row_no" };
    },
  },

  mounted() {
    this.getAcademicYear();
  },
};
